import React, { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import BlockList from "../components/LessonBlocks";
import { normalizeLesson, segmentSpeech } from "../lib/lessonModel";
import ReadAloud from "../components/ReadAloud";
import { StageLoading, ErrorState } from "../components/Loading";
import { C, RESOURCE } from "../lib/theme";
import { ArrowRight, RefreshCw, ExternalLink, ChevronDown, Check, Sparkles, Eye } from "lucide-react";
import { toast } from "sonner";

function resourceUrl(r) {
  if (r.url && /^https?:\/\//.test(r.url)) return r.url;
  if (r.type === "watch") return `https://www.youtube.com/results?search_query=${encodeURIComponent(r.searchQuery)}`;
  return `https://www.google.com/search?q=${encodeURIComponent(r.searchQuery)}`;
}

// Sections are revealed one at a time (segmenting: one idea, then a breath,
// then the next), so remember how far the learner got per session + attempt.
const unlockKey = (session) => `fathom_s1_unlock_${session.id}_a${session.attempt || 1}`;
const loadUnlocked = (session) => {
  try { return Math.max(1, parseInt(localStorage.getItem(unlockKey(session)), 10) || 1); } catch { return 1; }
};
const saveUnlocked = (session, n) => {
  try { localStorage.setItem(unlockKey(session), String(n)); } catch { /* per-browser convenience only */ }
};

// The System Map: the lesson's mechanism as a cause-to-effect chain that fills
// in as sections are completed (dual coding — the verbal lesson gets a spatial
// twin — and an advance organizer: the shape of the whole idea is visible from
// the start). Nodes of finished sections are lit and clickable.
function SystemMap({ sections, done, current, finished }) {
  if (!sections.length || sections.some((s) => !s.mapLabel)) return null;
  const jump = (id) => document.getElementById(`sec-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <div className="mt-5 rounded-lg border border-hair bg-surface p-4 hud-scan" data-testid="system-map">
      <div className="mb-3 font-mono text-[11px] uppercase tracking-wider text-muted">System map</div>
      <div className="flex flex-wrap items-center gap-y-2">
        {sections.map((sec, i) => {
          const isDone = i < done;
          const isCurrent = !finished && i === current;
          return (
            <React.Fragment key={sec.id}>
              {i > 0 && <span className="mx-1.5 select-none text-muted/60" aria-hidden="true">→</span>}
              <button
                data-testid={`map-node-${i}`}
                onClick={() => (isDone || finished) && jump(sec.id)}
                disabled={!isDone && !finished}
                className={`rounded-md border px-2.5 py-1 font-mono text-[11px] transition-colors ${isDone || finished ? "press cursor-pointer" : "cursor-default"}`}
                style={{
                  borderColor: isDone || finished ? C.blu : isCurrent ? "rgba(56,189,248,0.45)" : "var(--hair)",
                  background: isDone || finished ? "rgba(56,189,248,0.10)" : "transparent",
                  color: isDone || finished ? C.blu : isCurrent ? "var(--ink)" : "var(--muted)",
                  boxShadow: isCurrent ? `0 0 0 1px rgba(56,189,248,0.35)` : "none",
                }}
              >
                {isCurrent && !finished ? "▸ " : ""}{sec.mapLabel}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// "Lock it in": takeaways stay hidden until the learner has tried to say them —
// retrieval beats re-reading (the testing effect), so the reveal is earned.
function Takeaways({ items }) {
  const [shown, setShown] = useState(() => items.map(() => false));
  if (!items?.length) return null;
  return (
    <div className="mt-4 rounded-lg border border-hair bg-surface p-5" style={{ borderLeft: `3px solid ${C.lim}` }} data-testid="takeaways-block">
      <div className="font-mono text-[11px] uppercase tracking-wider" style={{ color: C.lim }}>Lock it in</div>
      <p className="mt-1 text-sm text-muted">From memory, say each one out loud (or in your head) — then reveal it and compare.</p>
      <div className="mt-3 space-y-2">
        {items.map((t, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <span className="mt-1 font-mono text-[10px] text-muted">{String(i + 1).padStart(2, "0")}</span>
            {shown[i] ? (
              <p className="snap-in text-ink/90" data-testid={`takeaway-${i}`}>{t}</p>
            ) : (
              <button
                data-testid={`takeaway-reveal-${i}`}
                onClick={() => setShown((s) => s.map((v, j) => (j === i ? true : v)))}
                className="press rounded-md border border-dashed border-hair px-3 py-1 text-sm text-muted hover:border-white/25 hover:text-ink"
              >
                <Eye className="mr-1.5 inline h-3.5 w-3.5" /> Reveal
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Stage1({ session, onComplete }) {
  const [lesson, setLesson] = useState(session.stage1.lesson);
  const [status, setStatus] = useState(session.stage1.status);
  const [focused, setFocused] = useState(session.stage1.focused);
  const [menuOpen, setMenuOpen] = useState(false);
  const [regenId, setRegenId] = useState(null);
  const [unlocked, setUnlocked] = useState(() => loadUnlocked(session));
  const [errorReason, setErrorReason] = useState(null);
  const poll = useRef(null);

  useEffect(() => {
    let active = true;
    const tick = async () => {
      const r = await api.getStage1(session.id);
      if (!active) return;
      setStatus(r.status);
      setFocused(r.focused);
      setErrorReason(r.errorReason || null);
      if (r.lesson) { setLesson(r.lesson); }
      if (r.status === "ready" || r.status === "error") { clearInterval(poll.current); }
    };
    if (!lesson || status === "pending" || status === "idle") {
      tick();
      poll.current = setInterval(tick, 3000);
    }
    return () => { active = false; clearInterval(poll.current); };
    // Poll setup belongs to the session, not to each lesson/status change —
    // tick() reads fresh values from the server anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  // Reloading alone was a dead end: the stored status stayed "error" and
  // nothing ever re-kicked generation. Ask the backend to regenerate first.
  const retry = async () => {
    setStatus("pending");
    setErrorReason(null);
    try { await api.stage1Retry(session.id); } catch (e) { /* poll shows the outcome */ }
    window.location.reload();
  };

  const regen = async (sectionId) => {
    setRegenId(sectionId);
    setMenuOpen(false);
    try {
      const newSeg = await api.regenSection(session.id, sectionId);
      setLesson((l) => {
        const key = l.segments ? "segments" : "sections";
        return { ...l, [key]: l[key].map((s) => (s.id === sectionId ? newSeg : s)) };
      });
      toast.success("Rebuilt that part with a simpler take.");
    } catch (e) {
      // Silently showing the old text back made a failed rebuild look like the
      // button did nothing. Say what happened and invite another go.
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Couldn't rebuild that part — tap Still fuzzy again.");
    } finally {
      setRegenId(null);
    }
  };

  const onCheckResult = (concept) => (ok) => { if (!ok) api.recordShaky(session.id, concept); };

  const nl = normalizeLesson(lesson);

  if (status === "error") {
    const detail = errorReason ? ` (${errorReason})` : "";
    return <ErrorState message={`The lesson couldn't be generated${detail}. Try again.`} onRetry={retry} />;
  }
  if (!nl) {
    return <StageLoading color={C.blu} title={focused ? "Rebuilding a focused review…" : "Building your lesson around the gaps we found…"} sub="This takes a few seconds. It's worth the wait." />;
  }

  const total = nl.segments.length;
  const visible = Math.min(unlocked, total);
  const finished = unlocked > total;
  const done = finished ? total : visible - 1;

  const advance = () => {
    const n = Math.min(unlocked + 1, total + 1);
    setUnlocked(n);
    saveUnlocked(session, n);
  };
  const showAll = () => { setUnlocked(total + 1); saveUnlocked(session, total + 1); };
  const stillFuzzy = (sec) => {
    api.recordShaky(session.id, sec.concept).catch(() => {});
    regen(sec.id);
  };

  return (
    <div className="mx-auto max-w-2xl pb-24 anim-fade-up" data-testid="stage1">
      <div className="mb-1 font-mono text-xs" style={{ color: C.blu }}>
        Stage 1 · Learn {focused && "· Focused review"}
      </div>
      <h2 className="font-display text-3xl sm:text-4xl">{nl.title}</h2>

      {/* progress: one idea at a time, and you can see the shore */}
      <div className="mt-4 flex items-center gap-3" data-testid="lesson-progress">
        <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(done / total) * 100}%`, background: C.blu }} />
        </div>
        <span className="mono-label tab-nums">{finished ? "ALL SECTIONS" : `SECTION ${visible}/${total}`}</span>
      </div>
      <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted/70">
        Method · generative learning — predict, connect, complete
      </div>

      <SystemMap
        sections={nl.segments}
        done={done}
        current={visible - 1}
        finished={finished}
      />

      <div className="mt-5 rounded-lg border border-hair bg-surface p-5" style={{ borderLeft: `3px solid ${C.blu}` }}>
        <div className="font-mono text-[11px] uppercase tracking-wider text-muted">In one sentence</div>
        <p className="mt-1 text-lg text-ink">{nl.oneSentence}</p>
      </div>

      <div className="mt-4">
        <div className="font-mono text-[11px] uppercase tracking-wider text-muted">Why this matters</div>
        <p className="mt-1 text-ink/90">{nl.whyItMatters}</p>
        <div className="mt-3"><ReadAloud text={`${nl.title}. ${nl.oneSentence}. Why this matters. ${nl.whyItMatters}`} color={C.blu} label="Read intro" /></div>
      </div>

      {/* prior knowledge first: new ideas stick to what is already known */}
      {nl.youAlreadyKnew && (
        <div className="mt-4 rounded-lg border border-hair bg-surface p-5" style={{ borderLeft: `3px solid ${C.vio}` }} data-testid="prior-block">
          <div className="font-mono text-[11px] uppercase tracking-wider" style={{ color: C.vio }}>You already knew this</div>
          <p className="mt-1 text-ink/90">{nl.youAlreadyKnew}</p>
        </div>
      )}

      {nl.segments.slice(0, visible).map((sec, i) => {
        const isCurrent = !finished && i === visible - 1;
        return (
        <section key={sec.id} id={`sec-${sec.id}`} className={`mt-8 scroll-mt-4 ${isCurrent ? "snap-in" : ""}`} data-testid={`lesson-section-${i}`}>
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-display text-xl text-ink">{sec.title}</h3>
            <ReadAloud text={segmentSpeech(sec)} color={C.blu} />
          </div>
          {sec.hook && (
            <p className="mt-1 font-display text-base italic" style={{ color: C.blu }} data-testid={`section-hook-${i}`}>
              {sec.hook}
            </p>
          )}
          {regenId === sec.id ? (
            <div className="mt-2 flex items-center gap-2 text-sm text-muted"><RefreshCw className="h-4 w-4 animate-spin" /> Re-teaching this part with simpler blocks…</div>
          ) : (
            <div className="mt-3 space-y-4" data-testid={`segment-blocks-${i}`}>
              <BlockList blocks={sec.blocks} onResult={onCheckResult(sec.concept)} />
            </div>
          )}

          {/* the gate: an honest self-judgment, not a formality — "fuzzy" feeds
              the same shaky-spot tracking the checks use, and re-teaches on the spot */}
          {isCurrent && (
            <div className="mt-5 flex flex-wrap items-center gap-2" data-testid={`section-gate-${i}`}>
              <button
                data-testid={`got-it-${i}`}
                onClick={advance}
                disabled={regenId === sec.id}
                className="press flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-bg disabled:opacity-50"
                style={{ background: C.grn }}
              >
                <Check className="h-4 w-4" /> {i === total - 1 ? "Got it — wrap up" : "Got it — continue"}
              </button>
              <button
                data-testid={`still-fuzzy-${i}`}
                onClick={() => stillFuzzy(sec)}
                disabled={regenId === sec.id}
                className="press flex items-center gap-2 rounded-full border border-hair px-5 py-2.5 text-sm text-ink hover:bg-white/5 disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" style={{ color: C.amb }} /> Still fuzzy — explain it differently
              </button>
            </div>
          )}
        </section>
        );
      })}

      {finished && (
        <>
          {/* analogy */}
          <div className="mt-8 rounded-lg border border-hair bg-surface p-5" style={{ borderLeft: `3px solid ${C.amb}` }} data-testid="analogy-block">
            <div className="font-mono text-[11px] uppercase tracking-wider" style={{ color: C.amb }}>The analogy</div>
            <p className="mt-1 text-ink/90">{nl.analogy.text}</p>
            <p className="mt-2 text-sm text-muted"><span className="text-ink/70">Where it breaks down: </span>{nl.analogy.whereItBreaks}</p>
          </div>

          {/* misconceptions */}
          <div className="mt-4 rounded-lg border border-hair bg-surface p-5" style={{ borderLeft: `3px solid ${C.ros}` }} data-testid="misconceptions-block">
            <div className="font-mono text-[11px] uppercase tracking-wider" style={{ color: C.ros }}>Where people go wrong</div>
            <div className="mt-2 space-y-3">
              {nl.misconceptions.map((m, i) => (
                <div key={i}>
                  <p className="text-sm text-ros/90">✗ {m.wrong}</p>
                  <p className="text-sm text-grn/90">✓ {m.right}</p>
                </div>
              ))}
            </div>
          </div>

          <Takeaways items={nl.keyTakeaways} />

          {/* resources */}
          <div className="mt-8">
            <div className="mb-3 font-mono text-[11px] uppercase tracking-wider text-muted">Take it further</div>
            <div className="grid gap-3 sm:grid-cols-2">
              {nl.resources.map((r, i) => {
                const meta = RESOURCE[r.type];
                return (
                  <a
                    key={i}
                    href={resourceUrl(r)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`resource-${r.type}`}
                    className="group rounded-lg border border-hair bg-surface p-4 transition-colors hover:bg-surface2"
                    style={{ borderTop: `2px solid ${meta.color}` }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[11px] uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
                      <ExternalLink className="h-3.5 w-3.5 text-muted group-hover:text-ink" />
                    </div>
                    <div className="mt-1 font-medium text-ink">{r.title}</div>
                    <div className="text-xs text-muted">{r.source} · {r.duration}</div>
                    <p className="mt-2 text-sm text-ink/70">{r.whatToLookFor}</p>
                  </a>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* actions */}
      <div className="sticky bottom-0 z-10 -mx-4 mt-10 flex flex-wrap items-center gap-3 border-t border-hair bg-bg px-4 py-4 shadow-[0_-8px_20px_rgba(11,13,18,0.9)] sm:mx-0 sm:px-0">
        {finished ? (
          <>
            <button
              data-testid="ready-to-teach-btn"
              onClick={onComplete}
              className="flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-bg transition-transform hover:scale-[1.03]"
              style={{ background: C.amb }}
            >
              I'm ready to teach it <ArrowRight className="h-4 w-4" />
            </button>
            <div className="relative">
              <button
                data-testid="reexplain-menu-btn"
                onClick={() => setMenuOpen((o) => !o)}
                className="flex items-center gap-1.5 rounded-full border border-hair px-4 py-3 text-sm text-ink hover:bg-white/5"
              >
                Re-explain a section <ChevronDown className="h-4 w-4" />
              </button>
              {menuOpen && (
                <div className="absolute bottom-full left-0 mb-2 w-64 rounded-lg border border-hair bg-surface2 p-1 shadow-xl">
                  {nl.segments.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => regen(s.id)}
                      data-testid={`reexplain-${s.id}`}
                      className="block w-full rounded-md px-3 py-2 text-left text-sm text-ink/90 hover:bg-white/5"
                    >
                      {s.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <span className="mono-label">One section at a time sticks better</span>
            <button
              data-testid="show-full-lesson-btn"
              onClick={showAll}
              className="press ml-auto rounded-full border border-hair px-4 py-2 text-sm text-muted hover:bg-white/5 hover:text-ink"
            >
              Show the full lesson
            </button>
          </>
        )}
      </div>
    </div>
  );
}
